// Copyright (c) StrangeBee 2023

import PreviewButton from '@Atoms/Buttons/PreviewButton/PreviewButton';
import { IconLabel } from '@Atoms/Info';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import client from '@Client';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { useDrawer } from '@Hooks/useDrawer';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THEditable } from '@Molecules/Inputs/THEditable/THEditable';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { CaseDrawer } from '@Organisms/Case/CaseDrawer/CaseDrawer';
import CaseNumber from '@Organisms/Case/CaseNumber/CaseNumber';
import { computeEventList } from '@Organisms/Case/eventList';
import { DisplayEventProps } from '@Organisms/Timeline/DisplayEvent';
import { EventCard } from '@Organisms/Timeline/EventCard';
import { useListAllUsers } from '@Queries/userQueries';
import React, { ReactElement } from 'react';
import { CaseOutput } from 'thehive-sdk';

import './CaseContent.scss';

export const CaseContent = (props: DisplayEventProps): ReactElement => {
    const i = useLocalMessage();
    const { data: userList = [] } = useListAllUsers();
    const { currentEvent, caseData, refetch } = props;
    const { title, number = 0, severity, assignee = '' } = caseData || {};
    const { visible, setVisible } = useDrawer<CaseOutput>({});

    return (
        <EventCard {...props}>
            <div className="case-content card__content">
                <div className="case-content__header card__header">
                    <div className="case-content__title">
                        <CaseNumber hashtag caseNumber={number} severity={severity} />
                        {title}
                    </div>
                </div>

                {assignee && (
                    <THEditable
                        name="assignee"
                        className="card-info__user"
                        initialValues={{ assignee }}
                        label={i('common.assignee')}
                    >
                        <THSelect disabled borderless>
                            {userList?.map(({ _id, name, avatar, login }) => (
                                <THSelect.Option key={_id} value={login}>
                                    <IconLabel
                                        key={_id}
                                        icon={<UserAvatar name={name} avatar={avatar} />}
                                        label={name}
                                    />
                                </THSelect.Option>
                            ))}
                        </THSelect>
                    </THEditable>
                )}

                {currentEvent.date && (
                    <THEditable
                        name="date"
                        className="card-info__date"
                        initialValues={{ date: currentEvent.date }}
                        label={i('common.start-date')}
                    >
                        <THDatePicker disabled borderless />
                    </THEditable>
                )}

                {caseData && (
                    <div className="card__preview">
                        <PreviewButton onClick={() => setVisible(true)} />
                    </div>
                )}

                {visible && (
                    <CaseDrawer
                        onVisibleChange={setVisible}
                        visible={visible}
                        data={caseData as CaseOutput}
                        eventList={computeEventList({
                            controller: client.CaseController,
                            locale: i,
                            onSuccess: (_, message) => {
                                message && openMessage('success', message);
                                refetch?.();
                            },
                            onError: handleNotificationError,
                        })}
                        onEditCounter={() => refetch?.()}
                    />
                )}
            </div>
        </EventCard>
    );
};
